import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { TextField } from '@mui/material';

import { Dialog, LocationDetails } from 'common/components';

import { postCustomTarget } from 'store/actions/campaigns';
import { fetchBoundary, setSnackbarError, setSnackbarSuccess } from 'store/actions/global';

import { addBoundaryToMap } from 'utils/helpers';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

const InlineFieldsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const PrefixField = styled(TextField)`
  flex: 0 0 120px;
`;

const NameField = styled(TextField)`
  flex: 1;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  margin-bottom: 16px;
`;

const InputFieldsWrapper = styled.div`
  padding-top: 16px;
`;

const LocationDetailsWrapper = styled.div`
  padding-top: 16px;
  padding-bottom: 16px;
`;

const CustomTargetDialog = ({ handleDialogClose, isDialogOpen, onSuccess }) => {
  const dispatch = useDispatch();
  const { organizationUuid } = useParams();

  // const mapContainer = useRef(null);
  const map = useRef(null);

  const [prefix, setPrefix] = useState('');
  const [name, setName] = useState('');
  const [officeTitle, setOfficeTitle] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [contactSource, setContactSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialLocationFilterData = {
    boundariesList: [],
    boundaryState: '',
    boundaryType: '',
    editedGeojsonUrls: [],
  };
  const [locationFilterData, setLocationFilterData] = useState(initialLocationFilterData);
  const [, setBoundaryValue] = useState('');

  const handleClose = () => {
    setPrefix('');
    setName('');
    setOfficeTitle('');
    setContactMethod('');
    setContactSource('');
    setIsSubmitting(false);
    setLocationFilterData(initialLocationFilterData);
    setBoundaryValue('');
    handleDialogClose();
  };

  const handleAddBoundary = geojsonUrl => {
    dispatch(fetchBoundary(geojsonUrl)).then(feature => {
      addBoundaryToMap(map.current, feature);

      const bounds = new mapboxgl.LngLatBounds();

      feature.geometry.coordinates[0][0].forEach(coordinate => {
        bounds.extend(coordinate);
      });

      map.current.fitBounds(bounds, {
        padding: {
          bottom: 25,
          left: 15,
          right: 5,
          top: 10,
        },
      });
    });
  };

  const handleAddTarget = () => {
    const boundaryUuid = locationFilterData.boundariesList[0]?.uuid || null;

    const targetData = {
      boundary: boundaryUuid,
      contact_method: contactMethod.trim() || '',
      contact_source: contactSource.trim() || '',
      name: name.trim(),
      office: officeTitle.trim(),
      organization: organizationUuid,
      prefix: prefix.trim() || '',
    };

    setIsSubmitting(true);

    dispatch(postCustomTarget(targetData))
      .then(() => {
        dispatch(setSnackbarSuccess({ message: 'Custom target request submitted successfully!' }));
        onSuccess?.();
        handleClose();
      })
      .catch(error => {
        setIsSubmitting(false);
        const errorMessage =
          error?.contactSource ||
          error?.detail ||
          'Failed to create custom target. Please try again.';

        dispatch(setSnackbarError({ message: errorMessage }));
      });
  };

  const isFormValid = () => {
    // Name and office are required
    if (!name.trim() || !officeTitle.trim()) {
      return false;
    }

    // If email/form is provided, contact source is required
    if (contactMethod.trim() && !contactSource.trim()) {
      return false;
    }

    return true;
  };

  return (
    <Dialog
      confirmationText={isSubmitting ? 'Adding...' : 'Add Target'}
      disabledButton={!isFormValid() || isSubmitting}
      handleConfirm={handleAddTarget}
      handleDialogClose={handleClose}
      isDialogOpen={isDialogOpen}
      title="Request Custom Target"
    >
      <p>
        For petition-only advocacy you can add non-governmental organizations, private corporations,
        and their respective officers. For consideration in message-delivery action centers, targets
        must be (i) governmental officials or bodies with (ii) a publicly shared communication
        channel (iii) that has a reasonable expectation of receiving and processing public comment.
        These targets are subject to approval.
      </p>
      <InputFieldsWrapper>
        <InlineFieldsContainer>
          <PrefixField
            label="Prefix"
            placeholder="Dr., Gov., etc."
            onChange={event => setPrefix(event.target.value)}
            value={prefix}
            variant="outlined"
          />
          <NameField
            label="Full name"
            required
            onChange={event => setName(event.target.value)}
            value={name}
            variant="outlined"
          />
        </InlineFieldsContainer>
        <StyledTextField
          label="Office title"
          required
          onChange={event => setOfficeTitle(event.target.value)}
          placeholder="Federal Regional Office, CEO of Acme Corp, etc."
          value={officeTitle}
          variant="outlined"
        />
        <LocationDetailsWrapper>
          <LocationDetails
            handleAddBoundary={handleAddBoundary}
            locationFilterData={locationFilterData}
            organizationUuid={organizationUuid}
            setFilterValue={setBoundaryValue}
            setLocationFilterData={setLocationFilterData}
          />
          <p style={{ fontSize: '0.8rem' }}>
            Optionally choose a geo-targetable boundary. Defaults to entire United States.
          </p>
        </LocationDetailsWrapper>
        <StyledTextField
          label="Email/form - omit this for auto-approved, petition only targets"
          onChange={event => setContactMethod(event.target.value)}
          value={contactMethod}
          variant="outlined"
        />
        {contactMethod.trim() && (
          <StyledTextField
            label="Where was this contact information sourced?"
            helperText="Include a publicly accessible URL. The data source should be published by the associated governmental body. Only targets with an official expectation of direct public feedback will be approved."
            required
            onChange={event => setContactSource(event.target.value)}
            value={contactSource}
            variant="outlined"
            multiline
            rows={2}
          />
        )}
      </InputFieldsWrapper>
    </Dialog>
  );
};

CustomTargetDialog.propTypes = {
  handleDialogClose: PropTypes.func.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
};

CustomTargetDialog.defaultProps = {
  onSuccess: undefined,
};

export default CustomTargetDialog;
